<template>
    <div class="flex flex-col gap-4 w-full">
        <h2 class="text-xl">Переводы</h2>
        <Transaction :readOnly="$currentUser.readOnly"/>
    </div> 
</template>

<script>
import Transaction from '@/pages/transactions/components/Transaction.vue';
import { mapGetters } from 'vuex'; 
import Select from 'primevue/select';
import AutoComplete from 'primevue/autocomplete';
import FloatLabel from 'primevue/floatlabel';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button'; 
import Spinner from '@/components/UI/Spinner.vue';
import { PhysicalPersonsService } from "@/services/physical-persons";
import { CurrenciesService } from "@/services/currencies";
import { TransfersService } from "@/services/transfers";
import { appendAlert } from '@/plugins/alert';

export default {
    name: 'TransactionsPage',
    components: {
        AutoComplete,
        FloatLabel, 
        Select,
        Button, 
        Spinner,
        InputNumber,
        Textarea,
        Transaction
    },
    
    data() {
        return {
            currentPayer: null, 
            prevCurrentPayer: null,
            payers: [],
            isLoadingPayers: false, 
            currentRecipient: null,
            prevCurrentRecipient: null,
            recipients: [],
            isLoadingRecipients: false,
            allPhysicalPersonsList: [],
            amount: null,
            amountError: null, 
            currency: null,
            currencies: [],
            description: '',
            isLoading: true, 
            isSubmitting: false            
        }
    },

    computed: {
        ...mapGetters('ui', ['isMobile']),

        referenceContent() {
            return this.getReferenceContent();
        }
    },

    created() {
        this.setUpComponent();
    },    

    methods: {   
        setUpComponent() { 
            this.setOptions();                       
        }, 

        async setOptions() {
            this.isLoading = true; 
            try {
                const { data: physicalPersonResult } =  await PhysicalPersonsService.getPesonsList();
                const { data: currenciesResult } =  await CurrenciesService.getCurrencies();
                this.currencies = currenciesResult;
                this.currency = currenciesResult[0];
                const sortedPhysicalPersonResult = physicalPersonResult?.sort((a, b) => a.name.localeCompare(b.name))
                this.allPhysicalPersonsList = sortedPhysicalPersonResult
                this.payers = sortedPhysicalPersonResult
                this.recipients = sortedPhysicalPersonResult
                this.currentPayer = sortedPhysicalPersonResult[0]
                this.prevCurrentPayer = sortedPhysicalPersonResult[0]
                this.currentRecipient = sortedPhysicalPersonResult[1] 
                this.prevCurrentRecipient = sortedPhysicalPersonResult[1]                        
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false
            }
        },

        handleAmountChange(event) {
            this.amount = event.value

            if (event.value === null || event.value <= 0) {
                this.amountError = 'Сумма должна быть больше нуля.'
            } else {
                this.amountError = null
            }
        },    

        payerChange(event) {
            this.prevCurrentPayer = event.value;
            this.$refs.autoCompletePayerRef?.hide();
        },

        recipientChange(event) {
            this.prevCurrentRecipient = event.value;
            this.$refs.autoCompleteRecipientRef?.hide();
        },

        restorePreviousPayer() {
            if (!this.currentPayer && this.prevCurrentPayer) {
                this.currentPayer = this.prevCurrentPayer 
            }
            this.$refs.autoCompletePayerRef?.hide();
        },

        restorePreviousRecipient() { 
            if (!this.currentRecipient && this.prevCurrentRecipient) {
                this.currentRecipient = this.prevCurrentRecipient 
            }
            this.$refs.autoCompleteRecipientRef?.hide();
        },

        clearCurrentPayer() {
            this.currentPayer = null
            this.payers = [...this.allPhysicalPersonsList];
            this.$refs.autoCompletePayerRef?.show();
        },

        clearCurrentRecipient() {
            this.currentRecipient = null
            this.recipients = [...this.allPhysicalPersonsList];
            this.$refs.autoCompleteRecipientRef?.show();
        },

        onFocusPayerInput() {
            this.currentPayer = null;
            this.payers = [...this.allPhysicalPersonsList];
            this.$refs.autoCompletePayerRef?.show();
        },

        onFocusRecipientInput() {
            this.currentRecipient = null;
            this.recipients = [...this.allPhysicalPersonsList];
            this.$refs.autoCompleteRecipientRef?.show();
        },

        onCompletePayerSearch() {
           this.payers = [...this.payers]
        },

        onCompleteRecipientSearch() {
           this.recipients = [...this.recipients]
        },

        async onSearchPayer(event) { 
            if (event?.value?.length >= 3) {
                this.isLoadingPayers = true
                try {            
                    const { data: physicalPersonResult } =  await PhysicalPersonsService.getPesonsList(event.value);
                    this.payers = [...physicalPersonResult.sort((a, b) => a.name.localeCompare(b.name))];
                } catch (e) {                
                    this.error = e.message
                    console.log(e)                
                    return
                } finally {
                    this.isLoadingPayers = false
                }
            } else {
                this.payers = this.allPhysicalPersonsList;
                this.isLoadingPayers = false
            }
        },

        async onSearchRecipient(event) { 
            if (event?.value?.length >= 3) {
                this.isLoadingRecipients = true
                try {            
                    const { data: physicalPersonResult } =  await PhysicalPersonsService.getPesonsList(event.value);
                    this.recipients = [...physicalPersonResult.sort((a, b) => a.name.localeCompare(b.name))];
                } catch (e) {                
                    this.error = e.message
                    console.log(e)                
                    return
                } finally {
                    this.isLoadingRecipients = false
                }
            } else {
                this.recipients = this.allPhysicalPersonsList;
                this.isLoadingRecipients = false
            }
        },

        async sendTransaction() {  
            this.isSubmitting = true
            try {   
                await TransfersService.send(this.currentPayer.guid, this.currentRecipient.guid, this.currency.guid, this.amount, this.description);
                this.amount = null
                this.description = ''
            } catch (e) {                
                this.error = e.message
                console.log(e)                
                return
            } finally {
                this.isSubmitting = false
            } 
        },

        getReferenceContent() {
            const selectedCurrency = this.currency.name;
            const rate = this.currencies.find(item => item.name === "USDT")?.rate;
            if (!rate) return null;
            
            switch(selectedCurrency) {
                case 'RUB':
                    const usdt = this.currencies.find(item => item.name === "USDT");
                    if (!usdt) return null;
                    const usdtAmount = (this.amount / rate).toFixed(2);
                    return {
                        iconClass: 'pi pi-dollar icons',
                        text: `${usdt.name} справочно(${rate}): ${usdtAmount}`
                    };
                
                case 'USDT':
                    const rub = this.currencies.find(item => item.name === "RUB");
                    if (!rub) return null;
                    const rubAmount = (this.amount * rate).toFixed(2);
                    return {
                        iconClass: 'pi pi-ruble icons',
                        text: `${rub.name} справочно(${rate}): ${rubAmount}`
                    };
                
                default: return null;
            }
        }
    }
}

</script>

<style lang="scss" scoped>

.dropdown-buttons {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

@media (max-width: 768px) {
    .dropdown-buttons {
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
}

.p-error {
  color: #D32F2F;
}

.icons {
    color: var(--p-primary-color);
}

.pi.pi-ruble::before {
  content: "₽"; 
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
  padding-bottom: 4px;
}

</style>